import {QuotaBanner} from '@github-ui/agent-sessions/components/task-view/QuotaBanner'
import {useAgentQuotaSnapshot} from '@github-ui/agent-sessions/hooks/use-agent-quota-snapshot'
import {useTopLevelAgents} from '@github-ui/agent-sessions/hooks/useTopLevelAgents'
import {mentionsCopilot} from '@github-ui/copilot-comment-mention-telemetry-element/mention-detector'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {type ReactNode, useMemo} from 'react'
import {useCurrentUser, useRepository} from '../../stores/selectors'

// Use lookahead for trailing boundary to avoid consuming the separator between
// adjacent mentions (e.g. "@copilot @claude" would miss "@claude" otherwise).
const MENTION_PATTERN = /(^|[\s])@([\w-]+)(?:\[agent\])?(?=[\s\W]|$)/gi

/**
 * Extracts all @mention names from text (lowercased), excluding 'copilot'.
 */
export function extractNonCopilotMentions(text: string): string[] {
  const names: string[] = []
  for (const match of text.matchAll(MENTION_PATTERN)) {
    const name = match[2]?.toLowerCase()
    if (name && name !== 'copilot') names.push(name)
  }
  return names
}

/**
 * Detects whether the text mentions @copilot (fast-path, no network) or any
 * recognized 3P agent (validated against the repo's available agent list).
 */
function useMentionsAgent(text: string): boolean {
  const {hasCopilotAccess} = useCurrentUser()
  const {baseRepository} = useRepository()
  const nwo = `${baseRepository.ownerLogin}/${baseRepository.name}`

  const copilotMentioned = useMemo(() => mentionsCopilot(text), [text])
  const otherMentions = useMemo(() => extractNonCopilotMentions(text), [text])

  const {data: agents} = useTopLevelAgents(otherMentions.length > 0 && hasCopilotAccess ? nwo : undefined)

  return useMemo(() => {
    if (copilotMentioned) return true
    if (!agents || otherMentions.length === 0) return false
    return otherMentions.some(name =>
      agents.some(a => a.slug.toLowerCase() === name || a.display_name.toLowerCase().replace(/\s+/g, '-') === name),
    )
  }, [copilotMentioned, agents, otherMentions])
}

export type CopilotQuotaGateResult = {
  /** The banner element to render (null when nothing to show) */
  banner: ReactNode
  /** True when quota is exhausted and submission should be blocked */
  submitDisabled: boolean
}

/**
 * Hook that determines quota state for a given comment text. Returns a banner
 * to render and whether submission should be disabled.
 *
 * Only fetches quota data when a valid @copilot or recognized 3P agent mention
 * is detected and the user has Copilot access.
 *
 * Gated behind the `copilot_quota_banner_pr_files_changed` feature flag.
 * Fail-open: submitDisabled defaults to false when flag is off, no mention,
 * quota OK, or data not yet loaded.
 */
export function useCopilotQuotaGate(text: string): CopilotQuotaGateResult {
  const {hasCopilotAccess} = useCurrentUser()
  const enabled = copilotFeatureFlags.quotaBannerPrFilesChanged && copilotFeatureFlags.codingAgentTBBQuotaBanner
  const hasMention = useMentionsAgent(text)

  const {data: agentQuotaSnapshot} = useAgentQuotaSnapshot({enabled: enabled && hasMention && hasCopilotAccess})

  // Coerce to undefined when no mention is present so stale TanStack Query
  // cache doesn't leak a previous snapshot after the user removes the mention.
  const effectiveSnapshot = hasMention ? agentQuotaSnapshot : undefined

  const submitDisabled = Boolean(enabled && hasMention && effectiveSnapshot && !effectiveSnapshot.can_start_session)

  const shouldShowBanner =
    enabled &&
    hasMention &&
    effectiveSnapshot &&
    (!effectiveSnapshot.can_start_session || effectiveSnapshot.percent_remaining <= 0.25)

  const banner = shouldShowBanner ? (
    <div className="mt-2">
      <QuotaBanner agentQuotaSnapshot={effectiveSnapshot} />
    </div>
  ) : null

  return {banner, submitDisabled}
}
